import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { isPushOrPerformClient } from "../../../utils/user-helpers";

const {
  PRIMARIES,
  WEIGHT_UNITS,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const { exercisesVolume, query, search, feBranding = {} } = locals;
  const { period, units, mobileWV } = query;

  const period_short = PERIOD_FORMATS[period];

  const selectedMetricsVolume =
    query.type === "total"
      ? exercisesVolume
      : _.pick(exercisesVolume, [query.type]);
  const keys = _(selectedMetricsVolume)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = getChartLabels(keys, query, period);

  const selectedMetricsVolumeGroups = _.values(selectedMetricsVolume);
  const totalMetricVolumes = _.map(keys, (key) =>
    _.sumBy(selectedMetricsVolumeGroups, `${key}.volume`)
  );
  const totalVolumes = _.map(totalMetricVolumes, (volume) =>
    locals.fromMetric(volume, units)
  );
  const maxVolume = _.max(totalVolumes);
  const volumeSeries = _.map(selectedMetricsVolume, (metrics, primary) => {
    return {
      name: primary,
      className: primary,
      data: _.map(keys, (key) =>
        locals.fromMetric(_.get(metrics, `${key}.volume`, 0), units)
      ),
    };
  });

  const intensitySeries = _.chain(keys)
    .map((key, i) => {
      const total = totalMetricVolumes[i];
      const metrics = _.compact(_.map(selectedMetricsVolumeGroups, key));
      const average = _.reduce(
        metrics,
        (prev, metric) => prev + metric.average * (metric.volume / total),
        0
      );
      const peak = _.max(_.map(metrics, "peak")) || 0;
      return [
        { x: key, y: average },
        { x: key, y: peak },
      ];
    })
    .unzip()
    .value();

  const intensitySeriesValues = _.map(_.flatten(intensitySeries), "y");
  const intensitySeriesMax = Math.max(
    Math.round((_.max(intensitySeriesValues) * 100) / 10) * 10,
    100
  );
  const intensitySeriesMin = Math.min(
    Math.max(Math.round((_.min(intensitySeriesValues) * 100) / 10) * 10, 0),
    40
  );

  return (
    <main
      role="reports-wrapper"
      className="Main Metrics"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text={t("dashboard:metrics.calendarDayView")}
        classNames="mobile-hidden"
      />
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("dashboard:metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/volume-split-ico.svg"
              className="metrics-icon"
            />
            <div>
              <h3>{t("dashboard:volumeSplitCoreLift.title")}</h3>
              <p className="p4">
                {t("dashboard:volumeSplitCoreLift.description")}
              </p>
            </div>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("dashboard:metrics.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("dashboard:metrics.to")}</span>
              <DateInput
                name="end"
                placeholder={t("dashboard:metrics.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("dashboard:metrics.month"), value: "month" },
                  { text: t("dashboard:metrics.week"), value: "week" },
                  { text: t("dashboard:metrics.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("dashboard:volumeSplitCoreLift.kg"), value: "metric" },
                { text: t("dashboard:volumeSplitCoreLift.lbs"), value: "imperial" },
              ]}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("dashboard:volumeSplitCoreLift.total"), value: "total" },
                { text: t("dashboard:volumeSplitCoreLift.squat"), value: "squat" },
                { text: t("dashboard:volumeSplitCoreLift.bench"), value: "bench" },
                { text: t("dashboard:volumeSplitCoreLift.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetricsVolume) && (
              <div>
                <div className="legend">
                  <div className="line series-a">{t("dashboard:volumeSplitCoreLift.avgIntensity")}</div>
                  <div className="line series-b">{t("dashboard:volumeSplitCoreLift.peakIntensity")}</div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("dashboard:volumeSplitCoreLift.volume")}</div>
                  <div className="right">{t("dashboard:volumeSplitCoreLift.intensity")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          onlyInteger: true,
                          scaleMinSpace: 30,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                    />

                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: [] }}
                      options={{
                        width: "60px",
                        height: "380px",
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          type: process.browser && require("chartist").StepAxis,
                          stretch: true,
                          showGrid: false,
                          showLabel: true,
                          ticks: _.range(
                            intensitySeriesMin,
                            intensitySeriesMax + 10,
                            10
                          ).map((i) => `${i}%`),
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const avgIntensity = _.get(
                          _.find(intensitySeries[0], { x: key }),
                          "y",
                          0
                        );
                        const peakIntensity = _.get(
                          _.find(intensitySeries[1], { x: key }),
                          "y",
                          0
                        );
                        const totalVolume = totalVolumes[i];
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              <div>
                                {t("dashboard:volumeSplitCoreLift.avgIntensity")}{" "}
                                <b>{_.round(avgIntensity * 100)}%</b>
                              </div>
                              <div>
                                {t("dashboard:volumeSplitCoreLift.peakIntensity")}{" "}
                                <b>{_.round(peakIntensity * 100)}%</b>
                              </div>
                              {_.compact(
                                _.map(volumeSeries, ({ name, data }) => {
                                  if (!data[i]) return;
                                  return (
                                    <div key={name}>
                                      {t(`dashboard:volumeSplitCoreLift.${name}`)}{" "}
                                      <b>
                                        {locals.withWeight(data[i], units)} -{" "}
                                        {_.round((data[i] / totalVolume) * 100)}
                                        %
                                      </b>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        );
                      }}
                    />
                    {_.size(keys) >= 2 && (
                      <Chart
                        type="Line"
                        data={{ series: intensitySeries }}
                        options={{
                          width: `${_.size(keys) * 40}px`,
                          height: "380px",
                          showPoint: false,
                          chartPadding: { left: 35, right: 75 },
                          axisX: { showGrid: false, showLabel: false },
                          axisY: {
                            low: intensitySeriesMin / 100,
                            high: intensitySeriesMax / 100,
                            showGrid: false,
                            showLabel: false,
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const VolumeSplitCoreLift = (app) => {
  app.get(
    "/volume-split-core-lift",
    set("heading", "Volume Split By Core Lift"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;
      const { activeUser: user } = locals;
      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolume", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "volume-split-core-lift",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolume"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      if (isPushOrPerformClient(user) || user.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default VolumeSplitCoreLift;
